import React from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {gql, useQuery} from '@apollo/client';
import moment from 'moment';

import {Image, message, Skeleton, Space, Table, Tag} from 'antd';

import {client_info, NOT_FOUND} from '../../routes';

import CrmLayout from '../../components/CrmLayout';
import CrmCenteredTitle from '../../components/CrmCenteredTitle';
import CrmDisplayData from '../../components/CrmDisplayData';
import isImage from '../../helpers/isImageURL';

const QUERY = gql`
    query posting ($id: ID) {
        posting (id: $id) {
            data {
                id
                attributes {
                    createdAt
                    message
                    uid
                    status
                    provider
                    mediaUrl
                    media {
                        data {
                            attributes {
                                name
                                url
                            }
                        }
                    }
                }
            }
        }
    }
`;

const RECEIVERS = gql`
    query receivers ($filters: ReceiverFiltersInput, $pagination: PaginationArg = {}, $sort: [String] = []) {
        receivers (filters: $filters, pagination: $pagination, sort: $sort) {
            data {
                id
                attributes {
                    address
                    status
                    errorMessage
                    updatedAt
                    user {
                        data {
                            id
                            attributes {
                                firstName
                                lastName
                            }
                        }
                    }
                }
            }
        }
    }
`;

const STATUSES = {
    created: {name: 'Создано', color: 'default'},
    sending: {name: 'Отправляется', color: 'processing'},
    done: {name: 'Доставлено', color: 'success'},
    error: {name: 'Ошибка', color: 'error'},
};

const PROVIDERS = {
    telegram: 'Telegram',
    whatsapp: 'WhatsApp',
};

function StatusTag({status}) {
    const item = STATUSES[status] || {name: status, color: 'default'};
    return <Tag color={item.color}>{item.name}</Tag>;
}

const columns = [
    {
        title: 'Клиент',
        dataIndex: 'user',
        key: 'user',
        render: (user) => user?.data &&
            <Link to={client_info(user.data.id)}>
                {`${user.data.attributes.firstName || ''} ${user.data.attributes.lastName || ''}`}
            </Link>,
    },
    {
        title: 'Адрес',
        dataIndex: 'address',
        key: 'address',
    },
    {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        render: (status) => <StatusTag status={status}/>,
        filters: Object.keys(STATUSES).map((key) => ({text: STATUSES[key].name, value: key})),
        onFilter: (value, record) => record.status === value,
    },
    {
        title: 'Ошибка',
        dataIndex: 'errorMessage',
        key: 'errorMessage',
    },
    {
        title: 'Обновлено',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value) => value && moment(value).format('DD.MM.YYYY HH:mm'),
    },
];

export default function PostingInfo() {

    const {id} = useParams();

    const navigate = useNavigate();

    const query = useQuery(QUERY, {
        variables: {id},
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data.posting.data === null) {
                navigate(NOT_FOUND, {replace: true});
            }
        },
        onError: (error) => {
            message.error(error.message);
            console.error(error);
        },
    });

    const posting = query.data?.posting?.data?.attributes;

    const receiversQuery = useQuery(RECEIVERS, {
        variables: {
            filters: {
                postingUid: {eq: posting?.uid},
            },
            pagination: {
                limit: 1000,
            },
            sort: ['id:asc'],
        },
        skip: !posting?.uid,
        pollInterval: 3000,
        onError: (error) => {
            message.error(error.message);
            console.error(error);
        },
    });

    const receivers = receiversQuery.data?.receivers?.data.map((item) => ({
        key: item.id,
        ...item.attributes,
    })) || [];

    const doneCount = receivers.filter((item) => item.status === 'done').length;
    const errorCount = receivers.filter((item) => item.status === 'error').length;

    const mediaUrl = posting?.mediaUrl || posting?.media?.data?.attributes?.url;
    const fileName = posting?.media?.data?.attributes?.name || mediaUrl?.split('/').pop();

    return (
        <CrmLayout>

            <CrmCenteredTitle title={`Рассылка #${id}`}/>

            {query.loading && <Skeleton active/>}

            {posting &&
            <Space direction={'vertical'} size={20} className={'block'}>

                <CrmDisplayData label={'Создана'} value={moment(posting.createdAt).format('DD.MM.YYYY HH:mm')}/>
                <CrmDisplayData label={'Канал'} value={PROVIDERS[posting.provider] || posting.provider}/>
                <CrmDisplayData label={'Статус'} value={<StatusTag status={posting.status}/>}/>

                {mediaUrl &&
                <CrmDisplayData
                    label={'Медиа'}
                    value={isImage(mediaUrl)
                        ? <Image src={mediaUrl} width={320} style={{borderRadius: 8}}/>
                        : fileName
                    }
                />
                }

                {posting.message &&
                <CrmDisplayData label={'Сообщение'} value={posting.message}/>
                }

                <CrmDisplayData
                    label={'Получатели'}
                    value={`Доставлено ${doneCount} из ${receivers.length}` + (errorCount ? `, ошибок ${errorCount}` : '')}
                />

                <Table
                    columns={columns}
                    dataSource={receivers}
                    loading={receiversQuery.loading}
                    pagination={{pageSize: 50, hideOnSinglePage: true}}
                    size={'small'}
                    scroll={{x: true}}
                />

            </Space>
            }

        </CrmLayout>
    );
}
